import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { TradeRestrictionLevelLabels } from '../../enums/trade-restriction-level.enum';
import { TradeRestrictionReason, TradeRestrictionReasonLabels } from '../../enums/trade-restriction-reason.enum';
import { SpinnerService } from '../../../xform-compat';
import { CreateTradeRestriction, Pagination, Person, TradeRestriction } from '../../models';
import { RestrictionService } from '../../../core/data-services/restriction.service';
import { RestrictionAddEditComponent } from '../restriction-add-edit/restriction-add-edit.component';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { CONFIG } from '../../../config';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { AuthenticationService, AuthorizationService } from '../../../core/services';
import { PERMISSIONS } from '../../enums/permissions.enum';
import { TradeRestrictionDataSource } from './TradeRestrictionDataSource';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompanyService } from '../../../core/data-services/company';
import { CompanySecurity } from '../../models/company/company-security.model';
import { DateTime } from 'luxon';
import { RestrictionsIntegrationService } from '../../../modules/restrictions/restrictions-integration.service';
import { skip } from 'rxjs/operators';
import { getRestrictionStaleMonths } from './restriction-staleness';

@Component({
  selector: 'rms-trade-restrictions-table',
  templateUrl: './trade-restrictions-table.component.html',
  styleUrls: ['./trade-restrictions-table.component.scss'],
  standalone: false
})
export class TradeRestrictionsTableComponent implements OnInit {

  @ViewChild(RestrictionAddEditComponent, { static: false }) restrictionAddEditComponent: RestrictionAddEditComponent;
  @ViewChild(DeleteConfirmationComponent, { static: false }) deleteConfirmationComponent: DeleteConfirmationComponent;
  @ViewChild('updateFailConfirm', { static: false }) updateFailConfirm: ConfirmationModalComponent;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  /** The header row as a native `<tr>`, for the Restrictions page's pinned copy on phones (rmsPinnedTableStack). */
  @ViewChild('pinnedHeadRow', { static: true }) pinnedHeadRow: TemplateRef<unknown>;

  private _filter: any = {};
  @Input() set filter(value: any) {
    this._filter = value;
    this.updateDisplayedColumns();
    if (this.dataSource) {
      this.pagination.page = 1;
      this.dataSource.setFilter(this._filter);
    }
  }

  private _tabLayout = false;
  /**
   * The company page's Restrictions/Board Seats tab: the columns that repeat the company come out, rows don't
   * expand, Person is plain text, and narrow tab columns get cards. The Restrictions page leaves this off.
   */
  @Input() set tabLayout(value: boolean) {
    this._tabLayout = value;
    this.updateDisplayedColumns();
  }

  @Output() personClick: EventEmitter<Person> = new EventEmitter<Person>();
  /** A sort tapped in the pinned header copy, so the page can bring the re-sorted rows into view. */
  @Output() pinnedSort: EventEmitter<void> = new EventEmitter<void>();
  /** A save or delete from this table succeeded, so totals the page keeps elsewhere may have changed. */
  @Output() recordsChanged: EventEmitter<void> = new EventEmitter<void>();

  public dataSource: TradeRestrictionDataSource;
  public pagination: Pagination;
  public totalCount = 0;
  public expandedRows: Set<number> = new Set();
  public companySecurities: Map<number, string> = new Map();
  // The current page's records and whether the first page has arrived, for the tab's cards and heading
  // True while a page of records is loading, for the Restrictions page's phone drawer (VIEW RESULTS)
  public loading = false;
  public records: Array<TradeRestriction> = [];
  public loaded = false;

  // Set by updateDisplayedColumns from the layout and the filter's Status
  public displayedColumns: Array<string> = [];

  private readonly destroyRef = inject(DestroyRef);

  public readonly TradeRestrictionLevelLabels = TradeRestrictionLevelLabels;
  public readonly TradeRestrictionReasonLabels = TradeRestrictionReasonLabels;
  public readonly TradeRestrictionReason = TradeRestrictionReason;
  public readonly PERMISSIONS = PERMISSIONS;

  // Sort ids for the sortable columns, where they differ from the column name; unlisted columns don't sort
  private readonly SORT_IDS: Record<string, string> = {
    'company.name': 'company.name', 'type': 'reason', 'netLevel': 'netLevel', 'person.lastName': 'person.lastName',
    'startDate': 'startDate', 'endDate': 'endDate', 'lastModified': 'updated'
  };

  protected readonly DateTime = DateTime;
  protected readonly CONFIG = CONFIG;

  constructor(
    public spinnerService: SpinnerService,
    public integrationService: RestrictionsIntegrationService,
    private restrictionService: RestrictionService,
    private translateService: TranslateService,
    private authorizationService: AuthorizationService,
    private authenticationService: AuthenticationService,
    private readonly companyService: CompanyService
  ) {
    const defaultPagination = {
      ...CONFIG.pagination,
      sort: 'company.name',
      direction: 'asc'
    };
    this.pagination = new Pagination(defaultPagination);
  }

  ngOnInit() {
    this.initDataSource();
    this.updateDisplayedColumns();
    this.integrationService.boardSeatUpdate$.subscribe(() => {
      this.dataSource.refresh();
    })
  }

  private updateDisplayedColumns() {
    if (this._tabLayout) {
      this.displayedColumns = ['type', 'person.lastName', 'details', 'startDate', 'lastModified'];
    } else {
      // End and Remove Reason only exist on ended records, so they show once Inactive or All is selected
      const includesEnded = this._filter?.active !== true;
      this.displayedColumns = [
        'company.name',
        'netLevel',
        'type',
        'person.lastName',
        'details',
        'startDate',
        ...(includesEnded ? ['endDate', 'endReason'] : []),
        'lastModified'
      ];
    }
  }

  public openRestrictionAddEdit(tradeRestriction?: TradeRestriction): void {
    if (this.spinnerService.active) {
      return;
    }
    const restrictionAddEditRef = this.restrictionAddEditComponent.openModal(tradeRestriction);
    restrictionAddEditRef.result.then((result: CreateTradeRestriction) => {
      if (result) {
        this.spinnerService.start();
        let operation$;

        if (!tradeRestriction?.active && result?.endDate) {
          operation$ = this.restrictionService.updateRestrictionEndDate(tradeRestriction.id,
            { "endDate": result.endDate.toUTC().toFormat(CONFIG.dateTimeServerFormatFullMillis) });
        } else if (tradeRestriction?.id) {
          operation$ = this.restrictionService.updateRestriction(tradeRestriction.id, result);
        } else {
          operation$ = this.restrictionService.createRestriction(result);
        }

        this.subscribeWithErrorHandling(operation$);
      }
    });
  }

  public deleteTradeRestriction(tradeRestriction: TradeRestriction) {
    if (this.spinnerService.active) {
      return;
    }
    const netLevel = TradeRestrictionLevelLabels[tradeRestriction.netLevel];
    const reasonLabel = TradeRestrictionReasonLabels[tradeRestriction.reason];
    this.deleteConfirmationComponent.data = {
      restrictionName: `${netLevel} ${reasonLabel} restriction from ${tradeRestriction.company.name}`,
      startDate: tradeRestriction.startDate
    };
    const deleteConfirmationRef = this.deleteConfirmationComponent.openModal(true);
    deleteConfirmationRef.result.then((result: { reason: string, endDate: DateTime, forceEzeError: boolean }) => {
      if (result) {
        const { reason, endDate, forceEzeError } = result;
        this.spinnerService.start();
        this.subscribeWithErrorHandling(
          this.restrictionService.deleteRestriction(tradeRestriction.id, reason, endDate, forceEzeError),
          (err: string) => this.translateService.instant(err)
        );
      }
    });
  }

  public hasFullPermission(restriction: TradeRestriction) {
    return this.authorizationService.hasPermission([PERMISSIONS.TradeRestrictionRead]) ||
      restriction.person.user.id === this.authenticationService.getLoggedUser().id;
  }

  /** Board-derived rows are maintained through their board seat, so they carry no actions of their own. */
  public isBoardDerived(restriction: TradeRestriction): boolean {
    return restriction.reason === TradeRestrictionReason.BOARD;
  }

  public canEdit(restriction: TradeRestriction): boolean {
    return !!this.integrationService.hasRestrictionPermission[PERMISSIONS.TradeRestrictionUpdate] && !this.isBoardDerived(restriction);
  }

  public canDelete(restriction: TradeRestriction): boolean {
    return restriction.boardSeatId === null && restriction.active &&
      !!this.integrationService.hasRestrictionPermission[PERMISSIONS.TradeRestrictionDelete];
  }

  /** Whether the row's kebab would have any items, so an empty menu is never offered. */
  public hasRowActions(restriction: TradeRestriction): boolean {
    return this.canEdit(restriction) || this.canDelete(restriction);
  }

  /** Months since an active restriction was last modified, when that's long enough to flag; otherwise null. */
  public getStaleMonths(restriction: TradeRestriction): number | null {
    return getRestrictionStaleMonths(restriction);
  }

  /** Header label for a column; shared by the real header row and the pinned copy. */
  public columnLabel(column: string): string {
    const labels: Record<string, string> = {
      'company.name': 'Company', 'netLevel': 'Net', 'type': 'Type', 'person.lastName': 'Person', 'details': 'Details',
      'startDate': this._tabLayout ? 'Since' : 'Start', 'endDate': 'End', 'endReason': 'Remove Reason', 'lastModified': 'Last Modified'
    };
    return labels[column] ?? '';
  }

  /** The CDK's own class for a column's cells, so the pinned copy's cells can be styled alike. */
  public columnClass(column: string): string {
    return `mat-column-${column.replace(/[^a-z0-9_-]/gi, '-')}`;
  }

  public isSortable(column: string): boolean {
    return !!this.SORT_IDS[column];
  }

  public isSortedBy(column: string): boolean {
    return !!this.sort && this.sort.active === this.SORT_IDS[column];
  }

  /** Company ascending; any other sort fills its header darker. */
  get isDefaultSort(): boolean {
    return this.sort?.active === 'company.name' && this.sort?.direction === 'asc';
  }

  /** Sorts from a tap in the pinned header copy, as a tap on the real header would. */
  public sortFromPin(column: string) {
    const id = this.SORT_IDS[column];
    if (!id) {
      return;
    }
    this.sort.sort({ id, start: 'asc', disableClear: false });
    this.pinnedSort.emit();
  }

  /** No pager when every row fits on one page. */
  get showPager(): boolean {
    return this.totalCount > this.pagination.pagesize;
  }

  get tabLayout(): boolean {
    return this._tabLayout;
  }

  public onPageChange() {
    if (this.dataSource) {
      this.dataSource.setPagination(this.pagination);
    }
  }

  /** The tab has no row expand: every row would expand to the same company's securities. */
  public onRowClick(restriction: TradeRestriction) {
    if (!this._tabLayout) {
      this.toggleRow(restriction);
    }
  }

  public toggleRow(restriction: TradeRestriction) {
    if (this.expandedRows.has(restriction.id)) {
      this.expandedRows.delete(restriction.id);
    } else {
      this.expandedRows.add(restriction.id);
      if (!this.companySecurities.has(restriction.company.id)) {
        this.companyService.getCompany(restriction.company.id).subscribe(resp => {
          this.companySecurities.set(restriction.company.id,
            this.getCompanyIdentifierLabel(resp.securities.filter(sec => sec.active)));
        })
      }
    }
  }

  private getCompanyIdentifierLabel(companySecurities: Array<CompanySecurity>): string {
    const tickerStrings: Array<string> = [];
    const cusips = new Set<string>();

    for (const sec of companySecurities) {
      if (sec.ticker) {
        tickerStrings.push(`${sec.ticker} (${sec.exchangeName})`);
      }
      if (sec.cusip) {
        cusips.add(sec.cusip);
      }
    }

    return `Tickers: ${tickerStrings.join(', ')} · CUSIPs: ${Array.from(cusips).join(', ')}`
  }

  /**
   * Subscribes to an observable with standardized error handling and completion logic.
   * Stops the spinner and refreshes the data source on completion.
   * Displays error modal on error.
   * @param operation$ The observable to subscribe to
   * @param errorTransform Optional function to transform error message before displaying
   */
  private subscribeWithErrorHandling(operation$: any, errorTransform?: (error: string) => string) {
    operation$.subscribe(
      () => {},
      (error: string) => {
        const errorMsg = errorTransform ? errorTransform(error) : error;
        this.handleSaveError(errorMsg);
      },
      () => {
        this.spinnerService.stop();
        this.dataSource.refresh();
        this.recordsChanged.emit();
      }
    );
  }

  private handleSaveError(errorMsg: string) {
    this.updateFailConfirm.body = errorMsg;
    this.updateFailConfirm.headerText = null;
    this.updateFailConfirm.cancelText = null;
    this.updateFailConfirm.confirmText = 'Okay';
    this.updateFailConfirm.openModal();
  }

  private initDataSource() {
    this.dataSource = new TradeRestrictionDataSource(this.restrictionService, this.sort, this.pagination);
    this.dataSource.setFilter(this.filter);
    this.dataSource.totalCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(total => {
        this.totalCount = total;
      });
    this.dataSource.records$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(records => {
        this.records = records;
        this.loaded = true;
      });
    this.dataSource.isLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        // Wrap in setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.loading = resp;
          if (resp) {
            this.spinnerService.start();
          } else {
            this.spinnerService.stop();
          }
        }, 0);
      })
  }

  get filter() {
    return this._filter;
  }
}
